import classNames from 'classnames';
import { isEmpty } from 'lodash';
import propTypes from 'prop-types';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { detectLanguage, translate } from '../../actions/communication';
import { errorMessage, hideModal } from '../../actions/internal';
import CardSelector from '../../components/communication/CardSelector';
import ProgramContentSelector from '../../components/communication/ProgramContentSelector';
import Recipients from '../../components/communication/Recipients';
import FilterCheckbox from '../../components/filters/FilterCheckbox';
import { LoadingSpinner } from '../../components/helpers/LoadingSpinner';
import localeNames from '../../utils/localeNames.jsx.erb';

const CommunicationMessageModal = ({
  filtersForGroups,
  filtersForGroupsReady,
  hideModal,
  errorMessage,
  onSendMessage,
  onSendMessageToGroups,
  detectLanguage,
  translation,
  translate,
  player,
  onModalClose,
  message = '',
  originCards = [],
  originProgramContentEntries = [],
  isSending = false,
  isTranslating = false,
  isDetecting = false,
  sendingComplete = false,
  forcePreview = false,
}) => {
  const [ages, setAges] = useState([]);
  const [forIndividuals, setForIndividuals] = useState(true);
  const [contentAvailable, setContentAvailable] = useState(true);
  const [cards, setCards] = useState(originCards);
  const [programContentEntries, setProgramContentEntries] = useState(originProgramContentEntries);
  const [aiTranslate, setAiTranslate] = useState(false);
  const [messageBody, setMessageBody] = useState(message);
  const [recipients, setRecipients] = useState([]);
  const [previewMode, setPreviewMode] = useState(forcePreview);
  const [translations, setTranslations] = useState([]);
  const [isEditingLanguage, setIsEditingLanguage] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isTranslateOnlyMode, setIsTranslateOnlyMode] = useState(false);

  useEffect(() => {
    if (sendingComplete) {
      hideModal();
    }
  }, [sendingComplete, hideModal]);

  useEffect(() => {
    const data = Object.entries(localeNames).map(([k, v]) => (
      {
        value: k,
        label: v,
      }
    ));
    setTranslations(data);

    if (player && message) {
      setIsTranslateOnlyMode(true);
      setPreviewMode(true);
      setMessageBody(message);
      setRecipients([{ value: player.playerId, label: player.playerName }]);
    }
  }, []);

  useEffect(() => {
    const modalElement = document.querySelector('.o-communication__modal');
    modalElement?.classList.toggle('o-communication__modal--preview', previewMode);
    if (previewMode) {
      detectLanguage({ text: messageBody });
      translate({ text: messageBody });
    }
  }, [previewMode]);

  useEffect(() => {
    if (isEmpty(translation?.translated)) {
      return;
    }
    const updatedTranslations = translations.map(t => {
      // Do not update the message for the original language
      if (t.value === translation?.detected?.code) {
        return { ...t, message: messageBody };
      }

      const message = translation.translated.find(m => m.language === t.value)?.text;
      return { ...t, message: message };
    });
    setTranslations(updatedTranslations);
  }, [translation?.translated]);

  const canSend = () => {
    return !(
      isEmpty(messageBody) ||
      isEmpty(recipients) ||
      isSending ||
      isDetecting ||
      isTranslating
    )
  }

  const sendMessage = () => {
    if (isSending) {
      return;
    }

    if (forIndividuals) {
      onSendMessage({
        cardIds: cards.map(c => c.value),
        contentAvailable,
        message: messageBody,
        playerIds: recipients.map(p => p.value),
        programContentEntryIds: programContentEntries.map(p => p.value),
        translations: translations,
      });
    } else {
      let cohortIds = [];
      let groupIds = [];
      let subgroupIds = [];

      recipients.forEach(recipientObj => {
        const [type, id] = recipientObj.value.split('_');
        switch (type) {
          case 'cohort':
            cohortIds.push(parseInt(id));
            break;
          case 'group':
            groupIds.push(parseInt(id));
            break;
          case 'subgroup':
            subgroupIds.push(parseInt(id));
            break;
        }
      })

      onSendMessageToGroups({
        age_ranges: ages.map(p => p.value),
        cardIds: cards.map(c => c.value),
        cohortIds,
        contentAvailable,
        groupIds,
        message: messageBody,
        programContentEntryIds: programContentEntries.map(p => p.value),
        subgroupIds,
        translations: translations,
      });
    }

    typeof onModalClose === 'function' ? onModalClose(false) : hideModal();
  };

  const updateTranslation = (value, message) => {
    const updatedTranslations = translations.map(t => {
      if (t.value === value) {
        return { ...t, message: message };
      }
      return t;
    })
    setTranslations(updatedTranslations);

  };

  /**
   * onCancelClick is an event handler function triggered when a cancel button is clicked.
   *
   * This function has the following logic:
   *  - If this is a Preview Mode that came from a regular modal window, we just switch the previewMode flag
   *  - If this is a Preview Mode that came from a Translate Only Mode, we call `onModalClose`
   *  to keep the input. And close the modal window.
   *  - Otherwise, we close the modal window.
   */
  const onCancelClick = () => {
    if (previewMode && !forcePreview) {
      return setPreviewMode(false);
    }

    if (forcePreview && typeof onModalClose === 'function') {
      onModalClose(true)
    }

    hideModal();
  }

  const handleRecipientsMode = (individual) => {
    const modalElement = document.querySelector('.o-communication__modal');
    modalElement?.classList.toggle('o-communication__modal--wide', !individual);
    setForIndividuals(individual);
    setRecipients([]); // reset already selected recipients
  };

  function renderRecipients() {
    return <>
      <div className="row u-margin-top--base u-margin-bottom--tiny">
        <div className="col-6">
          <input type="radio" checked={forIndividuals} onChange={() => handleRecipientsMode(true)}/>
          <label onClick={() => handleRecipientsMode(true)}>Message individual(s)</label>
        </div>
        <div className="col-6">
          <input type="radio" checked={!forIndividuals} onChange={() => handleRecipientsMode(false)}/>
          <label onClick={() => handleRecipientsMode(false)}>Message
            group(s)</label>
        </div>
      </div>
      <Recipients
        filters={{ ...filtersForGroups }}
        selectedIndividuals={recipients}
        isLoadingGroups={!filtersForGroupsReady}
        forGroups={!forIndividuals}
        onAgeChange={setAges}
        onRecipientsChange={setRecipients}
        onErrorMessage={errorMessage}/>
    </>;
  }

  function renderMessageForm() {
    return <>
      <div className="u-padding-bottom--tiny">
        <label>Message body:</label>
        <textarea
          className="u-padding-bottom--zero u-margin-bottom--zero" rows="5"
          value={messageBody}
          onChange={(e) => setMessageBody(e.target.value)}
          disabled={isSending}/>
      </div>
      <FilterCheckbox
        label="Translate"
        value={aiTranslate}
        onChange={(value) => {
        setAiTranslate(value)
      }}
        disabled={isSending}/>
    </>;
  }

  function renderOptional() {
    return <>
      <h2 className="u-padding-top--base u-txt--base u-txt--bold">Add content</h2>
      <CardSelector
        value={cards}
        onChange={(value) => { setCards(value) }}
        isDisabled={isSending || !isEmpty(programContentEntries)}
        onErrorMessage={errorMessage}
        className='u-margin-bottom--tiny'/>
      <ProgramContentSelector
        value={programContentEntries}
        onChange={(value) => { setProgramContentEntries(value) }}
        isDisabled={isSending || !isEmpty(cards)}
        onErrorMessage={errorMessage}/>
      <FilterCheckbox
        labelCss="margin-right-0"
        label="Make content available for those that don't have access"
        value={contentAvailable}
        onChange={(value) => {
        setContentAvailable(value)
      }}
        disabled={isSending}/>
    </>;
  }

  function renderTargets() {
    let targets;

    if (isTranslateOnlyMode) {
      targets = [{ label: player.playerName, value: player.playerId, accountName: player.accountName }]
    } else {
      targets = recipients.map(recipient => {
        if (forIndividuals) {
          return {
            label: recipient.label,
            value: recipient.value,
            accountName: recipient.accountName,
          }
        } else {
          return {
            label: recipient.label,
            value: recipient.value,
          }
        }
      });
    }

    const individuals = (recipient, idx) => {
      return(
        <span key={recipient.value}>
          {recipient.accountName} - caregiver of {recipient.label}
          {idx < targetsCount - 1 ? ', ' : ''}
        </span>
      )
    }

    const groups = (recipient, idx) => {
      return (
        <span key={recipient.value}>
          {recipient.label}
          {idx < targetsCount - 1 ? ', ' : ''}
        </span>
      )
    }

    const targetsCount = targets.length;
    return (
      <div>To:&nbsp;
        {targets.map((recipient, idx) => {
          return (
            forIndividuals ? individuals(recipient, idx) : groups(recipient, idx)
          )
        })}
      </div>
    );
  }

  function renderMessage() {
    return (
      <div className="u-margin-top--base">{messageBody}</div>
    );
  }

  function renderTranslationsBlock() {
    const message = (translation) => {
      if (isEditingLanguage !== translation.value) {
        return (<div>{translation.message}</div>)
      }

      return (
        <textarea
          className="u-padding-bottom--zero u-margin-bottom--zero u-margin-top--small" rows="5"
          value={translation.message || ''}
          onChange={(e) => updateTranslation(translation.value, e.target.value)}
          disabled={isSending}/>
      )
    }

    const buttons = (language) => {
      if (isEditingLanguage === language) {
        return (
          <button
            className="u-margin-bottom--zero o-sp-button o-sp-button--profile o-communication__button--edit"
            onClick={() => {
              setIsEditingLanguage('');
              setIsEditing(false);
            }}>
            <i className="fa fa-check" aria-hidden="true"></i>
            <span className="u-margin-left--tiny">Save</span>
          </button>
        )
      } else {
        return (
          <button
            className={classNames('u-margin-bottom--zero o-sp-button o-sp-button--profile o-communication__button--edit', {
              'o-communication__button--disabled': isEditing,
            })}
            disabled={isEditing}
            onClick={() => {
              setIsEditingLanguage(language);
              setIsEditing(true);
            }}>
            <i className="fa fa-pencil" aria-hidden="true"></i>
            <span className="u-margin-left--tiny">Edit</span>
          </button>
        )
      }
    }

    const canEdit = (language) => {
      if (isDetecting || isTranslating) {
        return false;
      }
      return language !== translation.detected?.code;
    }

    return (
      <div style={{ overflowY: 'auto', height: '300px' }}>
        <div className="u-txt--label u-padding-bottom--small text-justify u-txt">
          We used OpenAI GPT API to suggest translations for your message. The family will receive the message in the language they use on Sparkler.
          Please review/edit these translations as you see fit.
        </div>
        <h2 className="u-txt--base u-txt--bold">Translations</h2>
        {translations.map((t, idx) => {
          return (
            <div key={idx} className="u-padding-bottom--rem">
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <div className="u-padding-bottom--small u-txt--small-xs" style={{ fontWeight: '600' }}>
                  {t.label}
                </div>
                {canEdit(t.value) ? buttons(t.value) : ''}
              </div>
              {isTranslating ? <LoadingSpinner/> : message(t)}
            </div>
          )
        })}
      </div>
    );
  }

  function onSendPreviewClick() {
    if (aiTranslate && !previewMode) {
      setPreviewMode(true);
    } else {
      sendMessage()
    }
  }

  function renderControls() {
    const buttonTitle = () => {
      if (isSending) {
        return 'Sending...';
      } else if (previewMode) {
        return 'Send'
      } else {
        return aiTranslate ? 'Preview' : 'Send';
      }
    }

    return (<div className="u-padding-top--base">
      <button
        className={
          classNames('o-sp-button o-sp-button--update u-float--left u-margin-right--base', {
            'o-communication__button--disabled': !canSend(),
            'o-sp-button--primary': !(isEmpty(messageBody) || isEmpty(recipients) || isSending),
          })}
        disabled={!canSend()}
        onClick={onSendPreviewClick}>
        {buttonTitle()}
      </button>
      <button
        className="o-sp-button cs-txt-link--sp-blue"
        onClick={onCancelClick}>
        Cancel
      </button>
    </div>);
  }

  return (
    <div className="column small-12">
      <h1 className="u-txt--header"><b>{previewMode ? 'Message preview' : 'New Message'}</b></h1>
      {!previewMode && (
        <>
          {renderRecipients()}
          {renderMessageForm()}
          {renderOptional()}
        </>
      )}
      {previewMode && (
        <div className="u-margin-top--base u-margin-bottom--tiny">
          {renderTargets()}
          {renderMessage()}
          {renderOptional()}
          <hr/>
          {renderTranslationsBlock()}
        </div>
      )}
      {renderControls()}
    </div>
  )
};

CommunicationMessageModal.propTypes = {
  originCards: propTypes.array,
  originProgramContentEntries: propTypes.array,
  filtersForGroups: PropTypes.shape({
    ages: PropTypes.array,
    cohorts: PropTypes.array,
    groups: PropTypes.array,
    subgroups: PropTypes.array,
  }).isRequired,
  filtersForGroupsReady: PropTypes.bool.isRequired,
  onSendMessage: PropTypes.func.isRequired,
  onSendMessageToGroups: PropTypes.func.isRequired,
  isSending: PropTypes.bool,
  isTranslating: PropTypes.bool,
  isDetecting: PropTypes.bool,
  sendingComplete: PropTypes.bool,
  forcePreview: PropTypes.bool,
  detectLanguage: PropTypes.func.isRequired,
  translation: PropTypes.shape({
    detected: PropTypes.object,
    translated: PropTypes.array,
  }),
  player: PropTypes.shape({
    playerId: PropTypes.number,
    playerName: PropTypes.string,
    accountName: PropTypes.string,
  }),
  message: PropTypes.string,
  onModalClose: PropTypes.func,
};

const mapStateToProps = state => ({
  filtersForGroups: state.communicationFilters.filtersForGroups.filters || {},
  filtersForGroupsReady: state.communicationFilters.filtersForGroups.loaded && !state.communicationFilters.filtersForGroups.isLoading,
  isSending: state.communication?.isSending,
  isTranslating: state.communication?.isTranslating,
  isDetecting: state.communication?.isDetecting,
  sendingComplete: state.communication?.sendingComplete,
  translation: state.communication?.translation || {},
});

export default connect(mapStateToProps, {
  hideModal,
  detectLanguage,
  translate,
  errorMessage,
})(CommunicationMessageModal);
